/**
 * Admin day roster — who is coming on one date, across every program that meets that day.
 *
 * The pickup board is this week's after-school dismissal. This is the planning view beside
 * it: pick a date (it opens on the next day anything meets), and each program meeting then
 * gets a card with the children expected. Flex and per-day children who did not choose the
 * day are counted separately rather than listed, because nobody is waiting for them.
 */

import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AlertTriangle, ChevronLeft, ChevronRight, Printer, Search } from 'lucide-react';
import {
  useAdminDayRoster,
  useAdminRosterPrograms,
  type DayRosterChild,
  type DayRosterGroup,
  type DayRosterProgram,
} from '../../hooks/useAdminDayRoster';
import { isSessionDate, nextSessionDate, previousSessionDate } from '../../staff-app/programSessions';
import { formatGradeLabel } from '../../utils/rosterGrouping';
import { shortProgramLabel } from '../../utils/programLabel';
import { formatWeekdayDateDisplay, getTodayDateStringInBusinessTZ } from '../../utils/date';
import { shiftDateString } from '../../utils/schedule';
import { AttendanceStatusBadge } from '../../components/attendance/AttendanceStatusBadge';
import { ScrollTabs } from '../../staff-app/components/ScrollTabs';

const DATE_PARAM = 'date';
/** Which program card is shown, by group key; absent means all of them. */
const GROUP_PARAM = 'program';
const ALL_GROUPS = 'all';
/**
 * In the all-programs view, a card longer than this starts collapsed to its header. The
 * after-school card is a hundred names, and on a phone it buried every enrichment class
 * beneath it; a class of a dozen still opens in full.
 */
const COLLAPSE_OVER = 15;
const YMD = /^\d{4}-\d{2}-\d{2}$/;

const KIND_LABEL: Record<DayRosterGroup['kind'], string> = {
  'after-school': 'After school',
  enrichment: 'Enrichment',
  camp: 'Camp',
  other: 'Program',
};

/** The nearest day on or after (step 1) / before (step -1) `from` that any program meets. */
function nearestSessionDay(
  programs: readonly DayRosterProgram[],
  from: string,
  step: 1 | -1,
): string | null {
  let best: string | null = null;
  for (const program of programs) {
    const found = step === 1 ? nextSessionDate(program, from) : previousSessionDate(program, from);
    if (!found) continue;
    if (best === null || (step === 1 ? found < best : found > best)) best = found;
  }
  return best;
}

function ChildRow({ child, showProgram }: { child: DayRosterChild; showProgram: boolean }) {
  const context = [
    child.grade ? formatGradeLabel(child.grade) : null,
    child.school,
    child.roomNumber ? `Room ${child.roomNumber}` : null,
  ].filter(Boolean);

  return (
    <li className="flex items-start gap-2 px-3 py-1.5 text-sm">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-gray-900">{child.displayName}</span>
          {showProgram && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600">
              {shortProgramLabel(child.programName)}
            </span>
          )}
          {child.hasAllergies && (
            <span
              className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-semibold bg-red-50 text-red-700"
              title={child.allergiesDetails ?? 'Allergies on file'}
            >
              <AlertTriangle className="w-3 h-3" />
              {child.allergiesDetails || 'Allergy'}
            </span>
          )}
          {child.flexUnplanned && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
              title="Flex, and no day plan on file for this month — may or may not come"
            >
              Flex · no plan
            </span>
          )}
          {child.inAfterSchool && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
              After school
            </span>
          )}
        </div>
        {context.length > 0 && <p className="text-[11px] text-gray-500">{context.join(' · ')}</p>}
        {child.status === 'absent' && child.absenceReason && (
          <p className="text-[11px] text-red-700">{child.absenceReason}</p>
        )}
      </div>
      {child.status && <AttendanceStatusBadge status={child.status} />}
    </li>
  );
}

function GroupCard({
  group,
  collapsible,
  onOpen,
}: {
  group: DayRosterGroup;
  /** Start collapsed when the list is long — the all-programs view only. */
  collapsible: boolean;
  /** Switches the page to this program alone. */
  onOpen: () => void;
}) {
  const [showNotScheduled, setShowNotScheduled] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const showProgram = new Set(group.children.map(c => c.programName)).size > 1;
  const absent = group.children.filter(c => c.status === 'absent').length;
  const allergies = group.children.filter(c => c.hasAllergies).length;
  const flexNoPlan = group.children.filter(c => c.flexUnplanned).length;
  const collapsed = collapsible && !expanded && group.children.length > COLLAPSE_OVER;

  return (
    <section
      data-testid="day-roster-group"
      data-group={group.key}
      className="bg-white border border-gray-200 rounded-lg overflow-hidden break-inside-avoid"
    >
      <header className="px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-baseline gap-2">
          <h3 className="text-sm font-semibold text-gray-900 truncate">{group.label}</h3>
          <span className="ml-auto text-sm font-semibold tabular-nums text-gray-900">
            {group.children.length}
          </span>
        </div>
        <p className="text-[11px] text-gray-500">
          {KIND_LABEL[group.kind]}
          {group.timeRange && ` · ${group.timeRange}`}
          {absent > 0 && ` · ${absent} reported absent`}
        </p>
        {/* What a collapsed card still owes the reader: the counts that need acting on. */}
        {collapsed && (allergies > 0 || flexNoPlan > 0) && (
          <p className="text-[11px] text-gray-500">
            {[
              allergies > 0 ? `${allergies} with allergies` : null,
              flexNoPlan > 0 ? `${flexNoPlan} Flex with no plan` : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        )}
      </header>

      {collapsed && (
        <div className="flex flex-wrap gap-2 px-3 py-2 print:hidden">
          <button
            type="button"
            onClick={onOpen}
            className="px-2.5 py-1.5 text-xs font-medium rounded-md bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Open {group.label}
          </button>
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="px-2.5 py-1.5 text-xs font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Show all {group.children.length} here
          </button>
        </div>
      )}

      {group.children.length === 0 ? (
        <p className="px-3 py-3 text-xs text-gray-500">Nobody expected.</p>
      ) : (
        // A collapsed card still prints in full: paper has no "show all" button.
        <ul className={`divide-y divide-gray-100 ${collapsed ? 'hidden print:block' : ''}`}>
          {group.children.map(child => (
            <ChildRow key={child.enrollmentId} child={child} showProgram={showProgram} />
          ))}
        </ul>
      )}

      {group.notScheduled.length > 0 && (
        <div className="border-t border-gray-100 px-3 py-1.5 text-xs text-gray-500">
          <button
            type="button"
            onClick={() => setShowNotScheduled(v => !v)}
            className="hover:text-gray-700 underline underline-offset-2 print:no-underline"
            aria-expanded={showNotScheduled}
          >
            {group.notScheduled.length} enrolled but not scheduled this day
          </button>
          {showNotScheduled && (
            <p className="mt-1 text-gray-600">
              {group.notScheduled.map(c => c.displayName).join(', ')}
            </p>
          )}
        </div>
      )}
    </section>
  );
}

export function AdminDayRoster() {
  const today = getTodayDateStringInBusinessTZ();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const programsQuery = useAdminRosterPrograms();
  const programs = programsQuery.data;

  // The day on screen: the URL's, else today if anything meets today, else the next day
  // anything does — "who is coming" asked on a Saturday means Monday.
  const requested = searchParams.get(DATE_PARAM);
  const defaultDate = useMemo(() => {
    if (!programs) return today;
    if (programs.some(p => isSessionDate(p, today))) return today;
    return nearestSessionDay(programs, today, 1) ?? today;
  }, [programs, today]);
  const date = requested && YMD.test(requested) ? requested : defaultDate;

  const { data, isLoading, error } = useAdminDayRoster(date, programs);

  const setDate = (next: string) => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        params.set(DATE_PARAM, next);
        return params;
      },
      { replace: true },
    );
  };

  // One program at a time, chosen from the chip strip. A key that does not meet on this day
  // (a class carried over from another date) falls back to all of them.
  const requestedGroup = searchParams.get(GROUP_PARAM) ?? ALL_GROUPS;
  const selectedGroup = data?.groups.some(g => g.key === requestedGroup) ? requestedGroup : ALL_GROUPS;
  const selectGroup = (key: string) => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        if (key === ALL_GROUPS) params.delete(GROUP_PARAM);
        else params.set(GROUP_PARAM, key);
        return params;
      },
      { replace: true },
    );
  };

  const prevDay = programs ? nearestSessionDay(programs, shiftDateString(date, -1), -1) : null;
  const nextDay = programs ? nearestSessionDay(programs, shiftDateString(date, 1), 1) : null;

  const searching = search.trim().length > 0;
  const groups = useMemo(() => {
    const term = search.trim().toLowerCase();
    // A search covers every program whatever chip is selected: an admin looking for one
    // child does not know which class they are in.
    if (!term) {
      const all = data?.groups ?? [];
      return selectedGroup === ALL_GROUPS ? all : all.filter(g => g.key === selectedGroup);
    }
    return (data?.groups ?? [])
      .map(group => ({
        ...group,
        children: group.children.filter(c => c.displayName.toLowerCase().includes(term)),
        notScheduled: group.notScheduled.filter(c => c.displayName.toLowerCase().includes(term)),
      }))
      .filter(group => group.children.length > 0 || group.notScheduled.length > 0);
  }, [data?.groups, search, selectedGroup]);

  const navButton =
    'inline-flex items-center justify-center w-8 h-8 rounded-md border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500';

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <button
          type="button"
          className={navButton}
          onClick={() => prevDay && setDate(prevDay)}
          disabled={!prevDay}
          aria-label="Previous session day"
          title="Previous session day"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <input
          type="date"
          value={date}
          onChange={e => YMD.test(e.target.value) && setDate(e.target.value)}
          aria-label="Roster date"
          className="px-2 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
        <button
          type="button"
          className={navButton}
          onClick={() => nextDay && setDate(nextDay)}
          disabled={!nextDay}
          aria-label="Next session day"
          title="Next session day"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        {date !== today && (
          <button
            type="button"
            onClick={() => setDate(today)}
            className="px-2.5 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            Today
          </button>
        )}

        <div className="relative">
          <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Find a student"
            aria-label="Find a student"
            className="pl-8 pr-3 py-1.5 text-xs border border-gray-300 rounded-md w-44 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="flex-1" />

        <button
          type="button"
          onClick={() => window.print()}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <Printer className="w-3.5 h-3.5" /> Print
        </button>
      </div>

      <div className="text-sm text-gray-700">
        <span className="font-semibold">{formatWeekdayDateDisplay(date, { weekday: 'long' })}</span>
        {date > today && <span className="ml-2 text-xs text-sky-700">upcoming</span>}
        {date === today && <span className="ml-2 text-xs text-gray-500">today</span>}
        {data && (
          <span className="ml-2 text-xs text-gray-500 tabular-nums">
            · {data.groups.length} program{data.groups.length === 1 ? '' : 's'} meeting ·{' '}
            {data.childCount} child{data.childCount === 1 ? '' : 'ren'} expected
          </span>
        )}
      </div>

      {/* Every program meeting that day, with its headcount, one tap from its own list.
          Sticky, so the way to a different class stays in reach however far down the
          after-school list a phone has scrolled. */}
      {data && data.groups.length > 1 && !searching && (
        <ScrollTabs
          label="programs"
          activeKey={selectedGroup}
          className="sticky top-14 lg:top-0 z-20 -mx-4 bg-white border-b border-gray-200 print:hidden"
        >
          {[
            { key: ALL_GROUPS, label: 'All', count: data.childCount },
            ...data.groups.map(g => ({
              key: g.key,
              label: g.kind === 'after-school' ? g.label : shortProgramLabel(g.label),
              count: g.children.length,
            })),
          ].map(chip => {
            const active = chip.key === selectedGroup;
            return (
              <button
                key={chip.key}
                type="button"
                data-tab-key={chip.key}
                data-group-chip={chip.key}
                onClick={() => selectGroup(chip.key)}
                aria-current={active ? 'page' : undefined}
                className={`flex-shrink-0 flex items-baseline gap-1.5 py-2 px-3 border-b-2 text-sm font-medium whitespace-nowrap transition-colors ${
                  active
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {chip.label}
                <span className="text-xs text-gray-400 tabular-nums">{chip.count}</span>
              </button>
            );
          })}
        </ScrollTabs>
      )}

      {programsQuery.error || error ? (
        <div className="p-4 bg-red-50 text-red-700 rounded text-sm">
          Could not load the roster:{' '}
          {(programsQuery.error ?? error) instanceof Error
            ? (programsQuery.error ?? error)!.message
            : 'unknown error'}
        </div>
      ) : isLoading || programsQuery.isLoading ? (
        <div className="p-8 text-center text-gray-500">Loading roster…</div>
      ) : (data?.groups.length ?? 0) === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">
          No programs meet on this day.
        </div>
      ) : groups.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">No students match that search.</div>
      ) : (
        <div className="grid gap-3 grid-cols-1 md:grid-cols-2 xl:grid-cols-3 items-start">
          {groups.map(group => (
            <GroupCard
              key={group.key}
              group={group}
              collapsible={selectedGroup === ALL_GROUPS && !searching}
              onOpen={() => selectGroup(group.key)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
